import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * useQueryState
 * 将某个状态与 URL 查询参数同步。
 *
 * @param {string} key 查询参数名
 * @param {{
 *  defaultValue?: string,
 *  replace?: boolean,
 *  clearOnDefault?: boolean,
 *  parse?: (v: string) => string,
 *  serialize?: (v: string) => string,
 * }} options
 * @returns {[string, (next: string) => void]}
 */
export const useQueryState = (
  key,
  {
    defaultValue = '',
    replace = true,
    clearOnDefault = true,
    parse = (v) => v,
    serialize = (v) => v,
  } = {},
) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const value = useMemo(() => {
    const raw = searchParams.get(key)
    if (raw == null) return defaultValue
    try {
      return parse(raw)
    } catch (_e) {
      return defaultValue
    }
  }, [searchParams, key, defaultValue, parse])

  const setValue = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev)
          const serialized = serialize(next)
          if (clearOnDefault && serialized === defaultValue) {
            nextParams.delete(key)
          } else {
            nextParams.set(key, serialized)
          }
          return nextParams
        },
        { replace },
      )
    },
    [setSearchParams, key, defaultValue, replace, clearOnDefault, serialize],
  )

  return [value, setValue]
}

/**
 * useTabQuery
 * 专用于 Tabs 的快捷封装。
 *
 * @param {string} key 查询参数名，默认 'tab'
 * @param {string} defaultValue 默认值
 * @param {{ replace?: boolean, clearOnDefault?: boolean }} options
 */
export const useTabQuery = (key = 'tab', defaultValue = '', options = {}) => {
  return useQueryState(key, { defaultValue, ...options })
}
